import { generateQueryStringFromParams } from '@/utils/history';
import { ApiTagTypes, innovorderApi } from '../index';
import { ApiResponse } from '../request.types';
import {
    AddProductsToCategoryPayload,
    AddProductsToCategoryResponse,
    CreateCategoryPayload,
    CreateCategoryResponse,
    DeleteCategoryPayload,
    DeleteCategoryResponse,
    GetCategoriesPayload,
    GetCategoriesResponse,
    GetCategoryPayload,
    GetCategoryResponse,
    UpdateCategoriesLayoutPayload,
    UpdateCategoriesLayoutResponse,
    UpdateCategoryPayload,
    UpdateCategoryResponse,
} from './categories.type';

const categoriesApi = innovorderApi.injectEndpoints({
    endpoints: (build) => ({
        getCategories: build.query<GetCategoriesResponse, GetCategoriesPayload>({
            query: ({ menuId, limit, offset }) => {
                const queryParameters = generateQueryStringFromParams({ limit, offset });

                return {
                    url: `/menus/${menuId}/categories?${queryParameters}`,
                    method: 'GET',
                };
            },
            providesTags: (response, _, queryArgs) => {
                if (response) {
                    return [
                        ...response.map(({ categoryId }) => ({
                            type: ApiTagTypes.Category,
                            id: `${queryArgs.menuId}-${categoryId}`,
                        })),
                        { type: ApiTagTypes.Category, id: `${queryArgs.menuId}-LIST` },
                    ];
                }
                return [{ type: ApiTagTypes.Category, id: `${queryArgs.menuId}-LIST` }];
            },
            transformResponse: (response: ApiResponse<GetCategoriesResponse>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        getCategory: build.query<GetCategoryResponse, GetCategoryPayload>({
            query: ({ menuId, categoryId }) => ({
                url: `/menus/${menuId}/categories/${categoryId}`,
                method: 'GET',
            }),
            providesTags: (_, __, queryArgs) => {
                return [
                    { type: ApiTagTypes.Category, id: `${queryArgs.menuId}-${queryArgs.categoryId}` },
                    { type: ApiTagTypes.Category, id: `${queryArgs.menuId}-LIST` },
                ];
            },
            transformResponse: (response: ApiResponse<GetCategoryResponse>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        createCategory: build.mutation<CreateCategoryResponse, CreateCategoryPayload>({
            query: ({ ...body }) => {
                return {
                    url: `/menus/${body.menuId}/categories/`,
                    method: 'POST',
                    body,
                };
            },
            invalidatesTags: (_, __, queryArgs) => {
                return [{ type: ApiTagTypes.Category, id: `${queryArgs.menuId}-LIST` }];
            },
        }),
        updateCategory: build.mutation<UpdateCategoryResponse, UpdateCategoryPayload>({
            query: ({ ...body }) => {
                return {
                    url: `/menus/${body.menuId}/categories/${body.categoryId}`,
                    method: 'PUT',
                    body,
                };
            },
            invalidatesTags: (_, __, queryArgs) => {
                return [{ type: ApiTagTypes.Category, id: `${queryArgs.menuId}-LIST` }];
            },
        }),
        deleteCategory: build.mutation<DeleteCategoryResponse, DeleteCategoryPayload>({
            query: ({ menuId, categoryId }) => {
                return {
                    url: `/menus/${menuId}/categories/${categoryId}`,
                    method: 'DELETE',
                };
            },
            invalidatesTags: (_, __, queryArgs) => {
                return [{ type: ApiTagTypes.Category, id: `${queryArgs.menuId}-LIST` }];
            },
        }),
        addProductsToCategory: build.mutation<AddProductsToCategoryResponse, AddProductsToCategoryPayload>({
            query: ({ menuId, categoryId, ...body }) => ({
                url: `/menus/${menuId}/categories/${categoryId}/products`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_, __, { menuId }) => [
                { type: ApiTagTypes.Category, id: `${menuId}-LIST` },
                { type: ApiTagTypes.Product, id: `${menuId}-LIST` },
            ],
            transformResponse: (response: ApiResponse<AddProductsToCategoryResponse>) => response.data,
        }),
        updateCategoriesLayout: build.mutation<UpdateCategoriesLayoutResponse, UpdateCategoriesLayoutPayload>({
            query: ({ menuId, layout }) => {
                return {
                    url: `/menus/${menuId}/categories/layout`,
                    method: 'PUT',
                    body: {
                        layout,
                    },
                };
            },
            invalidatesTags: (_, __, queryArgs) => {
                return [{ type: ApiTagTypes.Category, id: `${queryArgs.menuId}-LIST` }];
            },
        }),
    }),
});

export const {
    useAddProductsToCategoryMutation,
    useUpdateCategoriesLayoutMutation,
    useGetCategoriesQuery,
    useDeleteCategoryMutation,
    useGetCategoryQuery,
    useCreateCategoryMutation,
    useUpdateCategoryMutation,
} = categoriesApi;
